/**
 * Assessment admin field labels, date-edit contracts, and contact-notes helpers.
 */

export const ASSESSMENT_FIELD_LABELS = {
  onsiteRemote: "Onsite / Remote",
  multiSponsor: "Multiple Sponsors Permitted",
  vendorIntroMeeting: "Vendor Intro Meeting",
  scopePlanningMeeting: "Scope Planning Meeting",
  firstDraftDeliveredByAuditor: "First Draft Delivered by Auditor",
  finalDraftDeliveredByAuditor: "Final Draft Delivered by Auditor",
  sponsorDraftUploaded: "Sponsor Draft Uploaded",
  sponsorDraftReportDelivered: "Sponsor Draft Report Delivered",
  sponsorDownloadOfDraftReport: "Sponsor Download of Draft Report",
  finalSponsorReportUploaded: "Final Sponsor Report Uploaded",
  publishedToLibrary: "Published to Library",
  observationReportDeliveredToVendor: "Observation Report Delivered to Vendor",
  observationResponseReceived: "Observation Response Received",
  observationReportCompleted: "Observation Report Completed",
  diligentNotes: "Diligent Notes",
  multiSponsorGrid: "Multiple Sponsors Permitted",
  requestedDraftReportDate: "Requested Draft Report Date",
  requestedDraftDateChangeReason: "Reason for Change",
};

/** System-managed dates: read-only in ManageAssessments (no DatePicker). */
export const SYSTEM_MANAGED_FORM_FIELDS = [
  "draftReviewDate",
  "FinalReportDate",
  "firstDraftUplaodedDate",
];

/** Admin-editable dates that keep a DatePickerController binding when updateActive. */
export const ADMIN_EDITABLE_FORM_FIELDS = [
  "requestDate",
  "introEmailDate",
  "introMeetingDate",
  "planningMeetingDate",
  "firstAssessmentDate",
  "assessmentCompletionDate",
  "agreedDraftDate",
  "firstDraftDelieveredSponsorDate",
  "firstDraftDeliveredByAuditorDate",
  "finalDraftDeliveredByAuditorDate",
  "draftDelieveredToVendorDate",
  "publishedToLibraryDate",
  "observationResponseReceivedDate",
  "observationReportCompletedDate",
];

export function normalizeContactEmail(email) {
  return String(email ?? "")
    .trim()
    .toLowerCase();
}

/**
 * Build notes map keyed by normalized email from API contacts.
 * @param {Array<{ email?: string, notes?: string|null }>|null|undefined} contacts
 * @returns {Record<string, string>}
 */
export function contactNotesMapFromContacts(contacts) {
  const map = {};
  (contacts ?? []).forEach((contact) => {
    const key = normalizeContactEmail(contact?.email);
    if (!key) return;
    map[key] = contact?.notes ?? "";
  });
  return map;
}

/**
 * Build minimal provider_contacts payload for admin Save (notes update by email).
 * @param {Array<object>|null|undefined} contacts - from ByVqa API
 * @param {Record<string, string>|null|undefined} notesByEmail
 * @returns {Array<object>}
 */
export function buildProviderContactsNotesPayload(contacts, notesByEmail = {}) {
  return buildContactsNotesPayload(contacts, notesByEmail);
}

/** Minimal contact payload for admin Save notes updates (vendor or sponsor). */
export function buildContactsNotesPayload(contacts, notesByEmail = {}) {
  return (contacts ?? [])
    .filter((contact) => normalizeContactEmail(contact?.email))
    .map((contact) => {
      const email = normalizeContactEmail(contact.email);
      const notes =
        notesByEmail?.[email] !== undefined
          ? notesByEmail[email]
          : (contact.notes ?? null);
      return {
        email,
        first_name: contact.first_name ?? null,
        last_name: contact.last_name ?? null,
        job_title: contact.job_title ?? null,
        is_request_primary: Boolean(contact.is_request_primary),
        is_ad_hoc: Boolean(contact.is_ad_hoc),
        sort_order: Number(contact.sort_order) || 0,
        notes: notes === "" ? null : notes,
      };
    });
}

/**
 * Primary sponsor row + contacts from admin assessment details.
 * @param {object|null|undefined} vqaDetails
 */
export function resolvePrimarySponsorContactContext(vqaDetails) {
  const sponsors =
    vqaDetails?.vqa_request_sponsors ||
    vqaDetails?.vqa_sponsors ||
    [];
  if (!Array.isArray(sponsors) || sponsors.length === 0) {
    return { sponsorId: null, contacts: [] };
  }
  const row =
    sponsors.find((s) => s.is_primary_sponsor) ||
    sponsors[0];
  return {
    sponsorId: row?.sponsor_id ?? null,
    contacts: row?.sponsor_contacts || row?.sponsorContacts || [],
  };
}

/**
 * Whether the intro meeting date picker should be shown in edit mode.
 * @param {boolean} updateActive
 * @param {boolean|string} introMeetingNotRequired
 */
export function shouldShowIntroMeetingDatePicker(
  updateActive,
  introMeetingNotRequired
) {
  if (!updateActive) return false;
  return !(
    introMeetingNotRequired === true ||
    introMeetingNotRequired === "true"
  );
}
